import React, { useEffect, useMemo, useState } from 'react';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from './ChartLayout';
import { KeyMetric, KeyMetrics } from './KeyMetrics';
import WastewaterGHGChart from './WastewaterGHGChart';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import {
  fetchScaledWastewaterSeries,
  type WastewaterActivitySnapshot,
} from '../../services/wastewaterInventoryService';
import { wastewaterMethodologyCopy } from './wastewaterMethodologyCopy';
import {
  WASTEWATER_EQUITY_CARDS,
  WASTEWATER_EQUITY_DESCRIPTION,
  WASTEWATER_OVERVIEW_DESCRIPTION,
  WASTEWATER_RELEVANCE_CARDS,
  WASTEWATER_RELEVANCE_DESCRIPTION,
  buildWastewaterOverviewNarrative,
  formatAcreFeet,
  formatKgPerResident,
  formatMtCo2e,
  formatTreatmentLevel,
} from './wastewaterMemberCopy';

const SERIES_YEARS = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);

export const WastewaterSection: React.FC<{ location: string }> = ({ location }) => {
  const [activeView, setActiveView] = useState('overview');
  const [metricLatest, setMetricLatest] = useState('—');
  const [metricLatestYear, setMetricLatestYear] = useState<number | null>(null);
  const [metricPerResident, setMetricPerResident] = useState('—');
  const [metricVolume, setMetricVolume] = useState('—');
  const [metricVolumeYear, setMetricVolumeYear] = useState<number | null>(null);
  const [metricTreatment, setMetricTreatment] = useState('—');
  const [narrative, setNarrative] = useState('Loading wastewater data...');
  const [seriesYears, setSeriesYears] = useState<number[]>(SERIES_YEARS);
  const [seriesValues, setSeriesValues] = useState<Array<number | null>>(SERIES_YEARS.map(() => null));
  const [storedYear, setStoredYear] = useState<number | null>(null);
  const [activity, setActivity] = useState<WastewaterActivitySnapshot | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        await supabaseLocationService.initialize();
        const geofips = await supabaseLocationService.getGeofips(location);
        if (!geofips) {
          if (!cancelled) setNarrative('No location selected.');
          return;
        }
        const result = await fetchScaledWastewaterSeries(Number(geofips), SERIES_YEARS);
        if (cancelled) return;
        if (!result.stored || !Object.keys(result.byYear).length) {
          setMetricLatest('—');
          setMetricPerResident('—');
          setMetricVolume('—');
          setMetricTreatment('—');
          setSeriesValues(SERIES_YEARS.map(() => null));
          setActivity(null);
          setNarrative(
            `${location} does not currently have a stored ClimatePlans wastewater inventory that can be scaled to the selected years.`
          );
          return;
        }

        const values = SERIES_YEARS.map((year) =>
          result.byYear[year] != null && Number.isFinite(result.byYear[year]) ? result.byYear[year] : null
        );
        const latestYear = [...SERIES_YEARS].reverse().find((year) => values[SERIES_YEARS.indexOf(year)] != null) ?? null;
        const latestMt = latestYear != null ? values[SERIES_YEARS.indexOf(latestYear)] : null;
        const storedMt = result.stored.mtCo2e;
        const basis = latestYear != null ? result.scaleBasisByYear[latestYear] : null;
        const population =
          latestYear != null ? result.populationByYear[latestYear] ?? null : null;

        setSeriesYears(SERIES_YEARS);
        setSeriesValues(values);
        setStoredYear(result.stored.inventoryYear);
        setActivity(result.activity);
        setMetricLatestYear(latestYear);
        setMetricLatest(latestMt != null ? formatMtCo2e(latestMt) : '—');
        setMetricPerResident(
          latestMt != null && population != null ? formatKgPerResident(latestMt, population) : '—'
        );
        setMetricVolumeYear(result.activity?.inventoryYear ?? result.stored.inventoryYear);
        setMetricVolume(
          result.activity?.treatmentVolumeAf != null ? formatAcreFeet(result.activity.treatmentVolumeAf) : '—'
        );
        setMetricTreatment(formatTreatmentLevel(result.activity?.treatmentLevel ?? null));

        setNarrative(
          buildWastewaterOverviewNarrative({
            location,
            latestYear,
            latestMt,
            storedYear: result.stored.inventoryYear,
            storedMt,
            scaleBasis: basis,
            activity: result.activity,
            population,
          })
        );
      } catch {
        if (!cancelled) setNarrative('Wastewater data could not be loaded for this location.');
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [location]);

  const hasSeries = useMemo(() => seriesValues.some((v) => v != null), [seriesValues]);

  return (
    <ChartLayout
      title="Wastewater"
      description="Process greenhouse gases from municipal wastewater treatment — a very small share of community emissions."
      chartType="line"
      keyMetrics={
        <KeyMetrics>
          <KeyMetric
            value={metricLatest}
            label={
              metricLatestYear
                ? `Process emissions (${metricLatestYear}, tCO₂e)`
                : 'Process emissions (tCO₂e)'
            }
            color="#0f766e"
          />
          <KeyMetric
            value={metricPerResident}
            label={
              metricLatestYear
                ? `Kilograms CO₂e per resident (${metricLatestYear})`
                : 'Kilograms CO₂e per resident'
            }
            color="#2563eb"
          />
          <KeyMetric
            value={metricVolume}
            label={
              metricVolumeYear ? `Treatment volume (${metricVolumeYear}, AF)` : 'Treatment volume (AF)'
            }
            color="#7c3aed"
          />
          <KeyMetric value={metricTreatment} label="Treatment level" color="#c2410c" />
        </KeyMetrics>
      }
      chartContent={
        hasSeries ? (
          <WastewaterGHGChart
            location={location}
            years={seriesYears}
            values={seriesValues}
            storedYear={storedYear}
          />
        ) : (
          <p>Wastewater emissions are not available for this location.</p>
        )
      }
      overviewContent={
        <OverviewContent title="Wastewater Overview" description={WASTEWATER_OVERVIEW_DESCRIPTION}>
          <p>{narrative}</p>
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent title="Relevance to Climate Action" description={WASTEWATER_RELEVANCE_DESCRIPTION}>
          {WASTEWATER_RELEVANCE_CARDS.map((card) => (
            <div key={card.title} className="methodology-details-simple">
              <h5>{card.title}</h5>
              <p>{card.body}</p>
            </div>
          ))}
        </RelevanceContent>
      }
      equityContent={
        <EquityContent title="Equity Considerations" description={WASTEWATER_EQUITY_DESCRIPTION}>
          {WASTEWATER_EQUITY_CARDS.map((card) => (
            <div key={card.title} className="methodology-details-simple">
              <h5>{card.title}</h5>
              <p>{card.body}</p>
            </div>
          ))}
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent title="Methodology" {...wastewaterMethodologyCopy(activity)} />
      }
      activeView={activeView}
      onViewChange={setActiveView}
      showRelevanceEquity={true}
      enableComments={true}
      location={location}
      appendLocationToTitle={true}
    />
  );
};
